// OwnerContactFormPage — /owner-contact/:token. PUBLIC, owner-facing.
//
// The 4th owner-onboarding step: after the EPMA is fully executed, each owner is
// emailed a per-owner tokenized link here to confirm WHO owns the property and HOW
// funds + the year-end 1099 + communication are handled. The first owner to Complete
// locks the intake, the Owner Contact Addendum auto-generates + goes out for
// signature, and that owner is deep-linked straight to their own sign page.
//
// Sibling of OwnerIntakeFormPage — a standalone branded surface (NOT wrapped in the
// authenticated AppShell, see App.tsx), talking ONLY to the public, token-gated
// /api/owner-contact/:token endpoints via lib/ownerContactClient.ts (no Bearer).
//
// SERVER-DRIVEN visibility: every field's applicability comes from the server's
// `applicable` object (the fork lives on the server — we render, we never recompute
// it). On a fork change we save + re-fetch so `applicable` (showDesignee /
// showPercentages) updates. On Complete we follow the returned view
// (generating → poll → forward; ready → deep-link to sign).
//
// Voice (per docs/bespoke-communications-doctrine.md): this owner has COMMITTED —
// their agreement is signed. The register is "you're with us; let's get your funds,
// statements, and 1099 exactly right," never re-selling, never re-asking what the
// signed agreement already settled (name/email shown to confirm, not to collect).
//
// SPEC §13 (married filing jointly; contact-only): `applicable` may carry
// showFundsSection / spousesQuestion / taxUnits / fundsFollowOwnership. Which funds
// section renders is lib/ownerContactFunds.fundsFlow: 'contact_only' (an existing
// owner's override intake — one line, no banking questions), 'units' (the spouses
// question + funds that follow ownership, components/ownerContact/FundsUnitsSection),
// or 'legacy' — a server without those fields, or a single owner — which is today's
// section exactly as it was.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  getOwnerContact,
  submitOwnerContact,
  OwnerContactApiError,
  type OwnerContactAnswers,
  type OwnerContactApplicable,
  type OwnerContactBackupContact,
  type OwnerContactFormView,
  type OwnerContactHandlingMode,
  type OwnerContactMemberContact,
  type OwnerContactOwnerAnswer,
  type OwnerContactSpouses,
} from '@/lib/ownerContactClient';
import { radioTileStyle, TextField } from '@/components/ownerContact/formBits';
import { FundsUnitsSection } from '@/components/ownerContact/FundsUnitsSection';
import { MemberPortalAccess } from '@/components/ownerContact/MemberPortalAccess';
import { mergeSplitGroup } from '@/lib/ownerContactRoster';
import {
  CONTACT_ONLY_FUNDS_LINE,
  contactOnlyIntro,
  fundsFlow,
  postableAnswers,
  rosterShapeKey,
} from '@/lib/ownerContactFunds';
import {
  CITY_UTILITY_ACCOUNT_NO_HELP,
  CITY_UTILITY_ACCOUNT_NO_LABEL,
  withCityUtilityAccountNo,
} from '@/lib/ownerContactCityUtility';

// Below this Bedrock split confidence we FLAG the split ("we read this as two
// people — is that right?") and offer a Merge-back. A firm split (or a non-split
// person) reads confidence 1 and is not flagged.
const LOW_CONFIDENCE = 0.85;

// ---------------------------------------------------------------------------
// Referral source chips. Values are human-readable (staff-facing capture; referral
// answers are intake-only, never printed in the addendum). "Someone referred us"
// reveals the optional referrer-detail field.
// ---------------------------------------------------------------------------

const REFERRAL_REFERRAL = 'Someone referred us';
const REFERRAL_SOURCES: Array<{ value: string; label: string }> = [
  { value: REFERRAL_REFERRAL, label: 'Someone referred us' },
  { value: 'Online search', label: 'Online search' },
  { value: 'Saw a sign', label: 'Saw a sign' },
];

// Polling for the generating→ready transition.
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_MS = 120000;
const PERCENT_EPSILON = 0.01;

// ---------------------------------------------------------------------------
// Pure answer helpers (immutable; never assign `undefined` — exactOptionalProperty).
// ---------------------------------------------------------------------------

/** A working owners[] array sized to the frozen roster, cloning existing answers. */
function ensureOwners(
  owners: OwnerContactOwnerAnswer[] | undefined,
  count: number,
): OwnerContactOwnerAnswer[] {
  const out: OwnerContactOwnerAnswer[] = [];
  for (let i = 0; i < count; i++) out.push({ ...(owners?.[i] ?? {}) });
  return out;
}

/** Set (or, when blank/undefined, DROP) one string field on owner `index`. */
function setOwnerText(
  answers: OwnerContactAnswers,
  count: number,
  index: number,
  key: 'phone' | 'email' | 'firstName' | 'lastName' | 'entityName' | 'signerTitle',
  value: string,
): OwnerContactAnswers {
  const owners = ensureOwners(answers.owners, count);
  const target = owners[index]!;
  const v = value.trim();
  if (v === '') delete target[key];
  else target[key] = v;
  return { ...answers, owners };
}

/** Immutably patch one entity member on owner `index` (members[] sub-list). */
function patchMember(
  answers: OwnerContactAnswers,
  count: number,
  index: number,
  memberIndex: number,
  patch: Partial<OwnerContactMemberContact>,
): OwnerContactAnswers {
  const owners = ensureOwners(answers.owners, count);
  const target = owners[index]!;
  const members = (target.members ?? []).map((m, i) =>
    i === memberIndex ? { ...m, ...patch } : m,
  );
  target.members = members;
  return { ...answers, owners };
}

/** Append a blank entity member row to owner `index`. */
function appendMember(
  answers: OwnerContactAnswers,
  count: number,
  index: number,
): OwnerContactAnswers {
  const owners = ensureOwners(answers.owners, count);
  const target = owners[index]!;
  target.members = [...(target.members ?? []), {}];
  return { ...answers, owners };
}

/** Remove entity member `memberIndex` from owner `index`. */
function removeMember(
  answers: OwnerContactAnswers,
  count: number,
  index: number,
  memberIndex: number,
): OwnerContactAnswers {
  const owners = ensureOwners(answers.owners, count);
  const target = owners[index]!;
  target.members = (target.members ?? []).filter((_, i) => i !== memberIndex);
  return { ...answers, owners };
}

/** Set (or DROP) one numeric percent field on owner `index`. */
function setOwnerPercent(
  answers: OwnerContactAnswers,
  count: number,
  index: number,
  key: 'ownershipPercent' | 'fundsPercent',
  value: number | undefined,
): OwnerContactAnswers {
  const owners = ensureOwners(answers.owners, count);
  const target = owners[index]!;
  if (value === undefined) delete target[key];
  else target[key] = value;
  return { ...answers, owners };
}

/** Set one boolean comms toggle on owner `index`. */
function setOwnerBool(
  answers: OwnerContactAnswers,
  count: number,
  index: number,
  key: 'receivesStatements' | 'receivesEmail',
  value: boolean,
): OwnerContactAnswers {
  const owners = ensureOwners(answers.owners, count);
  owners[index]![key] = value;
  return { ...answers, owners };
}

function parsePercent(s: string): number | undefined {
  const t = s.trim();
  if (t === '') return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? n : undefined;
}

/** Sum a percent column across the roster (undefined entries skipped) — a UX aid. */
function columnSum(
  owners: OwnerContactOwnerAnswer[] | undefined,
  count: number,
  key: 'ownershipPercent' | 'fundsPercent',
): number {
  let sum = 0;
  for (let i = 0; i < count; i++) {
    const v = owners?.[i]?.[key];
    if (typeof v === 'number' && Number.isFinite(v)) sum += v;
  }
  return sum;
}

function firstName(name: string | null | undefined): string {
  const n = (name ?? '').trim();
  if (!n) return '';
  return n.split(/\s+/)[0] ?? '';
}

// ---------------------------------------------------------------------------
// Shared branded pieces (mirrors OwnerIntakeFormPage; inlined per the sibling)
// ---------------------------------------------------------------------------

function BrandLockup({ propertyAddress }: { propertyAddress: string | null }): React.JSX.Element {
  return (
    <header style={{ textAlign: 'center', marginBottom: 'var(--space-7)' }}>
      <div
        style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 26,
          lineHeight: 1.2,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: 'var(--fg)',
        }}
      >
        Welcome&nbsp;Home
      </div>
      <div
        style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 13,
          lineHeight: 1.2,
          letterSpacing: '0.42em',
          textTransform: 'uppercase',
          color: 'var(--accent)',
          paddingLeft: '0.42em',
        }}
      >
        Properties
      </div>
      <div
        style={{
          width: 56,
          height: 1,
          backgroundColor: 'var(--accent)',
          margin: 'var(--space-4) auto 0',
        }}
      />
      {propertyAddress ? (
        <p
          style={{
            marginTop: 'var(--space-4)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-md)',
            color: 'var(--fg-muted)',
          }}
        >
          {propertyAddress}
        </p>
      ) : null}
    </header>
  );
}

function SectionCard(props: {
  title: string;
  description?: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section className="card" style={{ marginBottom: 'var(--space-5)' }}>
      <div style={{ marginBottom: 'var(--space-5)' }}>
        <h2
          style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 'var(--text-lg)',
            fontWeight: 400,
            color: 'var(--fg)',
            margin: 0,
          }}
        >
          {props.title}
        </h2>
        {props.description ? (
          <p className="modal-description" style={{ marginTop: 'var(--space-2)' }}>
            {props.description}
          </p>
        ) : null}
      </div>
      {props.children}
    </section>
  );
}


function ProcessingDots(): React.JSX.Element {
  return (
    <span aria-hidden="true" style={{ display: 'inline-flex', gap: 6, marginTop: 'var(--space-5)' }}>
      <style>{`
        @keyframes oc-dot { 0%, 80%, 100% { opacity: 0.25; } 40% { opacity: 1; } }
        .oc-dot { width: 8px; height: 8px; border-radius: 50%;
          background: var(--accent); display: inline-block;
          animation: oc-dot 1.2s ease-in-out infinite; }
        .oc-dot:nth-child(2) { animation-delay: 0.2s; }
        .oc-dot:nth-child(3) { animation-delay: 0.4s; }
        @media (prefers-reduced-motion: reduce) { .oc-dot { animation: none; opacity: 0.6; } }
      `}</style>
      <span className="oc-dot" />
      <span className="oc-dot" />
      <span className="oc-dot" />
    </span>
  );
}

const confirmHeadlineStyle: React.CSSProperties = {
  fontFamily: 'var(--font-serif)',
  fontSize: 26,
  fontWeight: 400,
  margin: 0,
  color: 'var(--fg)',
};

const confirmEyebrowStyle: React.CSSProperties = {
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--text-xs)',
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: 'var(--accent)',
  marginBottom: 'var(--space-3)',
};

/**
 * The bespoke closing card. `ready` → the addendum is out and this owner can sign
 * now ("Review & sign →"). `preparing` → it's being written up (a completion won the
 * lock, ours or a co-owner's) with the processing dots. `closed` → a gentle already-
 * complete/closed state (never a dead-end).
 */
function ClosingCard(props: {
  kind: 'ready' | 'preparing' | 'closed';
  name: string;
  address: string;
  signUrl?: string;
}): React.JSX.Element {
  const { kind, name, address } = props;
  const forAddress = address ? ` for ${address}` : '';
  const thanks = name ? `Thanks, ${name} — ` : 'Thanks — ';

  if (kind === 'ready') {
    return (
      <div className="card" style={{ textAlign: 'center' }}>
        <div style={confirmEyebrowStyle}>All set</div>
        <h1 style={confirmHeadlineStyle}>Your addendum is ready to sign.</h1>
        <p style={{ marginTop: 'var(--space-4)', color: 'var(--fg-muted)' }}>
          {thanks}we&apos;ve prepared your Owner Contact Addendum{forAddress}. Give it a quick look
          and add your signature — it only takes a moment.
        </p>
        {props.signUrl ? (
          <div style={{ marginTop: 'var(--space-5)' }}>
            <a className="btn" data-size="lg" href={props.signUrl} style={{ textDecoration: 'none' }}>
              Review &amp; sign &rarr;
            </a>
          </div>
        ) : (
          <p style={{ marginTop: 'var(--space-4)', color: 'var(--fg-muted)' }}>
            Check your email for the link to sign.
          </p>
        )}
      </div>
    );
  }

  if (kind === 'preparing') {
    return (
      <div className="card" style={{ textAlign: 'center' }} aria-busy="true">
        <div style={confirmEyebrowStyle}>All set</div>
        <h1 style={confirmHeadlineStyle}>We&apos;re preparing your addendum.</h1>
        <p style={{ marginTop: 'var(--space-4)', color: 'var(--fg-muted)' }}>
          {thanks}we have what we need and we&apos;re writing up your Owner Contact Addendum
          {forAddress} now. We&apos;ll email it to you to sign in just a moment — you can leave this
          page open.
        </p>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <ProcessingDots />
        </div>
      </div>
    );
  }

  // closed — nothing to do here right now (already handled, or the link has closed).
  return (
    <div className="card" style={{ textAlign: 'center' }}>
      <div style={confirmEyebrowStyle}>All set</div>
      <h1 style={confirmHeadlineStyle}>This is all taken care of.</h1>
      <p style={{ marginTop: 'var(--space-4)', color: 'var(--fg-muted)' }}>
        There&apos;s nothing left for you to do on this link right now. If we need anything else, we
        &apos;ll be in touch — or just reply to the email we sent you and a real person will help.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small field primitives
// ---------------------------------------------------------------------------


// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function OwnerContactFormPage(): React.JSX.Element {
  const { token = '' } = useParams<{ token: string }>();

  const [view, setView] = useState<OwnerContactFormView | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [problems, setProblems] = useState<string[]>([]);

  // The working answer set — local source of truth. Seeded from the loaded view;
  // every field change POSTs the WHOLE set (the server shallow-merges + replaces),
  // and we take the server's refreshed VIEW (state + applicable) authoritatively
  // while keeping our own answers (they equal what we just POSTed, so no clobber).
  const [answers, setAnswers] = useState<OwnerContactAnswers>({});
  const answersRef = useRef<OwnerContactAnswers>({});
  answersRef.current = answers;
  // The latest view's applicable model — what gets POSTed depends on it (§13: no
  // stale handling mode under the tax-unit model; see postableAnswers).
  const applicableRef = useRef<OwnerContactApplicable | null>(null);
  applicableRef.current = view?.applicable ?? null;

  // Which owner emails / names are being edited (confirm-don't-re-ask: read-only until Edit).
  const [editingEmail, setEditingEmail] = useState<Record<number, boolean>>({});
  const [editingName, setEditingName] = useState<Record<number, boolean>>({});
  // Single-owner "change" reveal for the optional account label.
  const [showAccountLabel, setShowAccountLabel] = useState(false);
  // "Add another owner" explanatory reveal (the roster is fixed by the signed EPMA).
  const [showAddOwnerNote, setShowAddOwnerNote] = useState(false);
  // Bumped when the owner reshapes the people roster (merge-back): the §13 funds
  // section remounts at once, before the refreshed roster arrives, so no couple pick
  // (index pairs) survives the indices moving. See rosterShapeKey.
  const [rosterVersion, setRosterVersion] = useState(0);

  // Post-submit outcome — replaces the form with a bespoke closing card.
  const [submitOutcome, setSubmitOutcome] = useState<
    { kind: 'ready'; signUrl?: string } | { kind: 'preparing' } | null
  >(null);

  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
  }, []);

  // --- Initial load ---
  useEffect(() => {
    let live = true;
    setLoading(true);
    setLoadError(null);
    getOwnerContact(token)
      .then((v) => {
        if (!live) return;
        setView(v);
        setAnswers(v.answers ?? {});
        if (v.state === 'ready') {
          setSubmitOutcome({ kind: 'ready', ...(v.signUrl ? { signUrl: v.signUrl } : {}) });
        }
      })
      .catch(() => {
        if (live) setLoadError('We could not load this form. The link may have expired.');
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [token]);

  // --- Save partial progress; take the server's refreshed view (re-computes the
  // applicable fork). Keeps local answers as the source of truth. ---
  const saveAndRefresh = useCallback(
    async (next: OwnerContactAnswers) => {
      setSaving(true);
      setSaveError(null);
      try {
        const res = await submitOwnerContact(token, {
          answers: postableAnswers(applicableRef.current, next),
        });
        if (!alive.current) return;
        // A progress save echoes a full view (has applicable). Fold it into `view`.
        setView((prev) => {
          const base: OwnerContactFormView =
            prev ??
            {
              state: 'fillable',
              propertyAddress: '',
              ownerNames: '',
              owner: null,
              applicable: null,
              answers: {},
            };
          return {
            ...base,
            state: res.state,
            ...(res.propertyAddress !== undefined ? { propertyAddress: res.propertyAddress } : {}),
            ...(res.ownerNames !== undefined ? { ownerNames: res.ownerNames } : {}),
            ...(res.owner !== undefined ? { owner: res.owner } : {}),
            ...(res.applicable !== undefined ? { applicable: res.applicable } : {}),
          };
        });
      } catch (err) {
        if (!alive.current) return;
        setSaveError(
          err instanceof OwnerContactApiError
            ? (err.errorMessage ?? 'We could not save that. Please try again.')
            : 'We could not save that. Please try again.',
        );
      } finally {
        if (alive.current) setSaving(false);
      }
    },
    [token],
  );

  /** Apply a mutation to answers, persist locally, and save. `refetch` saves+refreshes
   *  the applicable fork (fork/toggle/select); otherwise defer the save to blur. */
  const commit = useCallback(
    (next: OwnerContactAnswers, refetch: boolean) => {
      setProblems([]);
      setAnswers(next);
      if (refetch) void saveAndRefresh(next);
    },
    [saveAndRefresh],
  );

  /** Persist the latest answers (used on text-field blur — no fork change). */
  const saveCurrent = useCallback(() => {
    void saveAndRefresh(answersRef.current);
  }, [saveAndRefresh]);

  // --- Poll GET until ready, then surface the sign action in place (no dead-end). ---
  const startPolling = useCallback(() => {
    const startedAt = Date.now();
    const tick = () => {
      if (!alive.current) return;
      getOwnerContact(token)
        .then((v) => {
          if (!alive.current) return;
          setView(v);
          if (v.state === 'ready') {
            setSubmitOutcome({ kind: 'ready', ...(v.signUrl ? { signUrl: v.signUrl } : {}) });
            return;
          }
          if (Date.now() - startedAt < POLL_MAX_MS) {
            pollTimer.current = setTimeout(tick, POLL_INTERVAL_MS);
          }
        })
        .catch(() => {
          if (!alive.current) return;
          if (Date.now() - startedAt < POLL_MAX_MS) {
            pollTimer.current = setTimeout(tick, POLL_INTERVAL_MS);
          }
        });
    };
    pollTimer.current = setTimeout(tick, POLL_INTERVAL_MS);
  }, [token]);

  // --- Complete ---
  const handleComplete = useCallback(async () => {
    setSubmitting(true);
    setSaveError(null);
    setProblems([]);
    try {
      const res = await submitOwnerContact(token, {
        answers: postableAnswers(applicableRef.current, answersRef.current),
        complete: true,
      });
      if (!alive.current) return;
      // Winning completion → this owner can sign now (deep-link straight to /sign).
      if (res.signUrl || res.state === 'ready') {
        setSubmitOutcome({ kind: 'ready', ...(res.signUrl ? { signUrl: res.signUrl } : {}) });
        return;
      }
      // A completion (ours or a co-owner's) is being written up → preparing + poll.
      if (res.state === 'generating') {
        setSubmitOutcome({ kind: 'preparing' });
        startPolling();
        return;
      }
      // Still fillable (shouldn't happen after a valid complete) → keep the form.
      if (res.applicable !== undefined) {
        setView((prev) =>
          prev ? { ...prev, state: res.state, applicable: res.applicable ?? prev.applicable } : prev,
        );
      }
    } catch (err) {
      if (!alive.current) return;
      if (err instanceof OwnerContactApiError && err.status === 400) {
        const probs = err.problems;
        if (probs.length > 0) {
          setProblems(probs);
          setSaveError('Please finish the highlighted items before signing.');
        } else {
          setSaveError(err.errorMessage ?? 'Please review your answers and try again.');
        }
      } else {
        setSaveError(
          err instanceof OwnerContactApiError
            ? (err.errorMessage ?? 'We could not submit. Please try again.')
            : 'We could not submit. Please try again.',
        );
      }
    } finally {
      if (alive.current) setSubmitting(false);
    }
  }, [token, startPolling]);

  const shell = useCallback(
    (children: React.ReactNode): React.JSX.Element => (
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: 'var(--bg-alt)',
          padding: 'var(--space-7) var(--space-4)',
        }}
      >
        <div style={{ maxWidth: 720, margin: '0 auto' }}>{children}</div>
      </div>
    ),
    [],
  );

  // --- Loading / error / closing states ---
  if (loading) {
    return shell(
      <>
        <BrandLockup propertyAddress={null} />
        <p style={{ textAlign: 'center', color: 'var(--fg-muted)' }}>Loading…</p>
      </>,
    );
  }

  if (loadError || !view) {
    return shell(
      <>
        <BrandLockup propertyAddress={null} />
        <div className="alert" data-tone="warning">
          <div className="alert-body">
            <div className="alert-title">This link is no longer available</div>
            {loadError ?? 'Please reply to the email we sent you and a real person will help.'}
          </div>
        </div>
      </>,
    );
  }

  const owner = view.owner;
  const greetName = firstName(owner?.name);
  const address = view.propertyAddress || '';

  // After a successful submit we own the screen (bespoke closing card).
  if (submitOutcome) {
    return shell(
      <>
        <BrandLockup propertyAddress={view.propertyAddress || null} />
        <ClosingCard
          kind={submitOutcome.kind}
          name={greetName}
          address={address}
          {...(submitOutcome.kind === 'ready' && submitOutcome.signUrl
            ? { signUrl: submitOutcome.signUrl }
            : {})}
        />
      </>,
    );
  }

  if (view.state === 'generating') {
    return shell(
      <>
        <BrandLockup propertyAddress={view.propertyAddress || null} />
        <ClosingCard kind="preparing" name={greetName} address={address} />
      </>,
    );
  }

  if (view.state === 'ready') {
    return shell(
      <>
        <BrandLockup propertyAddress={view.propertyAddress || null} />
        <ClosingCard
          kind="ready"
          name={greetName}
          address={address}
          {...(view.signUrl ? { signUrl: view.signUrl } : {})}
        />
      </>,
    );
  }

  if (view.state === 'unavailable' || !view.applicable) {
    return shell(
      <>
        <BrandLockup propertyAddress={view.propertyAddress || null} />
        <ClosingCard kind="closed" name={greetName} address={address} />
      </>,
    );
  }

  // --- Fillable form ---
  const applicable: OwnerContactApplicable = view.applicable;
  const count = applicable.ownerCount;
  const flow = fundsFlow(applicable);
  const rawMode: OwnerContactHandlingMode | undefined = answers.disbursement?.mode;

  const ownershipSum = columnSum(answers.owners, count, 'ownershipPercent');
  const fundsSum = columnSum(answers.owners, count, 'fundsPercent');
  const ownershipOk = Math.abs(ownershipSum - 100) <= PERCENT_EPSILON;
  const fundsOk = Math.abs(fundsSum - 100) <= PERCENT_EPSILON;

  const soleName = applicable.perOwner[0]?.name || 'you';

  // Set the fork mode (one_handles | split) — saves + refreshes the applicable set.
  const setMode = (mode: OwnerContactHandlingMode) => {
    const disbursement = { ...(answers.disbursement ?? {}), mode };
    commit({ ...answers, disbursement }, true);
  };

  // one_handles designee: one person receives BOTH the funds AND the 1099.
  const setDesignee = (index: number) => {
    const disbursement = { ...(answers.disbursement ?? {}), mode: 'one_handles' as const, fundsOwnerIndex: index };
    commit({ ...answers, disbursement, taxReporting: { ownerIndex: index } }, true);
  };

  const setAccountLabel = (v: string) => {
    const disbursement = { ...(answers.disbursement ?? {}) };
    const t = v.trim();
    if (t === '') delete disbursement.fundsAccountLabel;
    else disbursement.fundsAccountLabel = t;
    setAnswers({ ...answers, disbursement });
  };

  // §13: the spouses answer (undefined = not answered yet) — saves + refreshes the
  // server's tax units.
  const setSpouses = (spouses: OwnerContactSpouses | undefined) => {
    const { spouses: _prev, ...rest } = answers;
    void _prev;
    commit(spouses ? { ...rest, spouses } : rest, true);
  };

  const setSource = (source: string) => {
    const referral = { ...(answers.referral ?? {}), source };
    commit({ ...answers, referral }, false);
    void saveAndRefresh({ ...answersRef.current, referral });
  };

  const setReferrerDetail = (v: string) => {
    const referral = { ...(answers.referral ?? {}) };
    const t = v;
    if (t.trim() === '') delete referral.referrerDetail;
    else referral.referrerDetail = t;
    setAnswers({ ...answers, referral });
  };

  // Backup contacts (optional, non-owner).
  const backups: OwnerContactBackupContact[] = answers.backupContacts ?? [];
  const setBackup = (index: number, patch: Partial<OwnerContactBackupContact>) => {
    const nextList = backups.map((b, i) => (i === index ? { ...b, ...patch } : b));
    setAnswers({ ...answers, backupContacts: nextList });
  };
  const addBackup = () => {
    const nextList: OwnerContactBackupContact[] = [
      ...backups,
      { name: '', relationship: '' },
    ];
    commit({ ...answers, backupContacts: nextList }, false);
  };
  const removeBackup = (index: number) => {
    const nextList = backups.filter((_, i) => i !== index);
    commit({ ...answers, backupContacts: nextList }, true);
  };

  // Merge-back a split group (collapse the sourceIndex group to one person). Restores
  // the raw EPMA name for that source. The roster shrinks → save+refresh so the server
  // re-derives the fork over it.
  const mergeSplit = (sourceIndex: number) => {
    const src = applicable.perOwner.find((p) => p.sourceIndex === sourceIndex);
    setEditingName({});
    setRosterVersion((v) => v + 1);
    commit(
      mergeSplitGroup(
        answers,
        sourceIndex,
        src ? { firstName: src.sourceFirstName, lastName: src.sourceLastName } : undefined,
      ),
      true,
    );
  };

  // Entity members/trustees (non-signing contacts collected when needsMemberCollection).
  const addEntityMember = (ownerIndex: number) => {
    commit(appendMember(answers, count, ownerIndex), false);
  };
  const setEntityMember = (
    ownerIndex: number,
    memberIndex: number,
    patch: Partial<OwnerContactMemberContact>,
  ) => {
    setAnswers(patchMember(answers, count, ownerIndex, memberIndex, patch));
  };
  const removeEntityMember = (ownerIndex: number, memberIndex: number) => {
    commit(removeMember(answers, count, ownerIndex, memberIndex), false);
  };
  // §14: owner-portal access for a member (a checkbox — saved at once, it has no blur).
  const setMemberPortalAccess = (ownerIndex: number, memberIndex: number, portalAccess: boolean) => {
    commit(patchMember(answers, count, ownerIndex, memberIndex, { portalAccess }), true);
  };

  const source = answers.referral?.source ?? '';

  return shell(
    <>
      <BrandLockup propertyAddress={view.propertyAddress || null} />

      <div style={{ marginBottom: 'var(--space-6)', textAlign: 'center' }}>
        <h1
          style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 26,
            fontWeight: 400,
            margin: 0,
            color: 'var(--fg)',
          }}
        >
          A few details{greetName ? `, ${greetName}` : ''}
        </h1>
        <p style={{ marginTop: 'var(--space-3)', color: 'var(--fg-muted)' }}>
          {flow === 'contact_only' ? (
            // Copy approved by Miles 2026-09-25 — no funds / 1099 promise: none is asked here.
            contactOnlyIntro(address)
          ) : (
            <>
          Your management agreement{address ? ` for ${address}` : ''} is signed — thank you. Let&apos;s
          confirm how to reach you and get your funds, statements, and year-end 1099 pointed to exactly
          the right place.
            </>
          )}
        </p>
      </div>

      {saveError ? (
        <div className="alert" data-tone="danger" style={{ marginBottom: 'var(--space-5)' }}>
          <div className="alert-body">
            {saveError}
            {problems.length > 0 ? (
              <ul style={{ margin: 'var(--space-2) 0 0', paddingLeft: 'var(--space-5)' }}>
                {problems.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* 1 — Confirm the people (the itemized roster is a SUGGESTION the owner confirms).
          Names are seeded read-only with Edit; low-confidence splits are flagged with a
          Merge-back; an entity can add its other members/trustees; phone is the blank. */}
      <SectionCard
        title="The owner(s)"
        description="This is who we have from your management agreement — just confirm each person, and add the best phone number to reach them."
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
          {applicable.perOwner.map((po) => {
            const oa = answers.owners?.[po.ownerIndex];
            const nameEditing = editingName[po.ownerIndex] ?? false;
            const emailEditing = editingEmail[po.ownerIndex] ?? false;
            const emailValue = oa?.email ?? po.email ?? '';
            const isEntity = po.kind === 'entity';
            const lowConfidence = po.fromSplit && po.confidence < LOW_CONFIDENCE;
            const members = oa?.members ?? [];
            return (
              <div
                key={po.role}
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-4)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--space-3)',
                }}
              >
                {/* Name — seeded + read-only until Edit (individual: first/last; entity:
                    legal name + signer title). */}
                {nameEditing ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                    {isEntity ? (
                      <>
                        <TextField
                          label="Entity legal name"
                          value={oa?.entityName ?? po.entityName ?? ''}
                          onChange={(v) =>
                            setAnswers(setOwnerText(answers, count, po.ownerIndex, 'entityName', v))
                          }
                          onBlur={saveCurrent}
                        />
                        <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                          <div style={{ flex: 1, minWidth: 160 }}>
                            <TextField
                              label="Signer's name"
                              value={`${oa?.firstName ?? ''} ${oa?.lastName ?? ''}`.trim()}
                              onChange={(v) => {
                                const parts = v.trim().split(/\s+/);
                                const last = parts.length > 1 ? parts[parts.length - 1]! : '';
                                const first = parts.length > 1 ? parts.slice(0, -1).join(' ') : (parts[0] ?? '');
                                let n = setOwnerText(answers, count, po.ownerIndex, 'firstName', first);
                                n = setOwnerText(n, count, po.ownerIndex, 'lastName', last);
                                setAnswers(n);
                              }}
                              onBlur={saveCurrent}
                            />
                          </div>
                          <div style={{ flex: 1, minWidth: 160 }}>
                            <TextField
                              label="Signer's title"
                              value={oa?.signerTitle ?? ''}
                              placeholder="e.g. Managing Member"
                              onChange={(v) =>
                                setAnswers(setOwnerText(answers, count, po.ownerIndex, 'signerTitle', v))
                              }
                              onBlur={saveCurrent}
                            />
                          </div>
                        </div>
                      </>
                    ) : (
                      <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: 140 }}>
                          <TextField
                            label="First name"
                            value={oa?.firstName ?? ''}
                            onChange={(v) =>
                              setAnswers(setOwnerText(answers, count, po.ownerIndex, 'firstName', v))
                            }
                            onBlur={saveCurrent}
                          />
                        </div>
                        <div style={{ flex: 1, minWidth: 140 }}>
                          <TextField
                            label="Last name"
                            value={oa?.lastName ?? ''}
                            onChange={(v) =>
                              setAnswers(setOwnerText(answers, count, po.ownerIndex, 'lastName', v))
                            }
                            onBlur={saveCurrent}
                          />
                        </div>
                      </div>
                    )}
                    <div>
                      <button
                        type="button"
                        className="btn"
                        data-variant="link"
                        data-size="sm"
                        onClick={() => {
                          setEditingName((m) => ({ ...m, [po.ownerIndex]: false }));
                          saveCurrent();
                        }}
                      >
                        Done
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      justifyContent: 'space-between',
                      gap: 'var(--space-3)',
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 600, color: 'var(--fg)' }}>{po.name || 'Owner'}</div>
                      <div className="helper" style={{ color: 'var(--fg-muted)' }}>
                        {isEntity ? 'Entity owner' : 'From your management agreement'}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn"
                      data-variant="link"
                      data-size="sm"
                      onClick={() => setEditingName((m) => ({ ...m, [po.ownerIndex]: true }))}
                    >
                      Edit
                    </button>
                  </div>
                )}

                {/* Low-confidence split — offer a Merge-back ("actually, one person"). */}
                {lowConfidence ? (
                  <div className="alert" data-tone="warning" style={{ margin: 0 }}>
                    <div className="alert-body">
                      We read your management agreement as two separate people here. If that&apos;s
                      right, just confirm below — otherwise this is one person.
                      <div style={{ marginTop: 'var(--space-2)' }}>
                        <button
                          type="button"
                          className="btn"
                          data-variant="secondary"
                          data-size="sm"
                          onClick={() => mergeSplit(po.sourceIndex)}
                        >
                          No — this is one person
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}

                {/* A CONFIDENT split still gets a quiet combine affordance (not just the
                    low-confidence warning) — a firm-but-wrong split needs a reject path. */}
                {po.fromSplit && !lowConfidence ? (
                  <div>
                    <button
                      type="button"
                      className="btn"
                      data-variant="link"
                      data-size="sm"
                      onClick={() => mergeSplit(po.sourceIndex)}
                    >
                      Actually, this is one person — combine
                    </button>
                  </div>
                ) : null}

                {emailEditing ? (
                  <TextField
                    label="Email"
                    type="email"
                    value={emailValue}
                    onChange={(v) =>
                      setAnswers(setOwnerText(answers, count, po.ownerIndex, 'email', v))
                    }
                    onBlur={saveCurrent}
                  />
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      justifyContent: 'space-between',
                      gap: 'var(--space-3)',
                    }}
                  >
                    <div>
                      <div className="helper" style={{ color: 'var(--fg-muted)', marginBottom: 2 }}>
                        Email
                      </div>
                      <div style={{ color: 'var(--fg)' }}>{emailValue || '—'}</div>
                    </div>
                    <button
                      type="button"
                      className="btn"
                      data-variant="link"
                      data-size="sm"
                      onClick={() =>
                        setEditingEmail((m) => ({ ...m, [po.ownerIndex]: true }))
                      }
                    >
                      Edit
                    </button>
                  </div>
                )}

                <TextField
                  label="Best phone number"
                  type="tel"
                  value={oa?.phone ?? ''}
                  placeholder="(509) 555-0123"
                  onChange={(v) =>
                    setAnswers(setOwnerText(answers, count, po.ownerIndex, 'phone', v))
                  }
                  onBlur={saveCurrent}
                />

                {/* Entity → collect the OTHER members/trustees (non-signing contacts). */}
                {po.needsMemberCollection ? (
                  <div
                    style={{
                      marginTop: 'var(--space-2)',
                      paddingTop: 'var(--space-3)',
                      borderTop: '1px dashed var(--border)',
                    }}
                  >
                    <div className="helper" style={{ color: 'var(--fg-muted)', marginBottom: 'var(--space-2)' }}>
                      Other members or trustees we should have on file for {po.entityName || 'this entity'}
                      {' '}(optional — they don&apos;t sign anything).
                    </div>
                    {members.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                        {members.map((m, mi) => (
                          <div
                            key={mi}
                            style={{
                              border: '1px solid var(--border)',
                              borderRadius: 'var(--radius-md)',
                              padding: 'var(--space-3)',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 'var(--space-2)',
                            }}
                          >
                            <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                              <div style={{ flex: 1, minWidth: 140 }}>
                                <TextField
                                  label="Name"
                                  value={`${m.firstName ?? ''} ${m.lastName ?? ''}`.trim()}
                                  onChange={(v) => {
                                    const parts = v.trim().split(/\s+/);
                                    const last = parts.length > 1 ? parts[parts.length - 1]! : '';
                                    const first = parts.length > 1 ? parts.slice(0, -1).join(' ') : (parts[0] ?? '');
                                    setEntityMember(po.ownerIndex, mi, { firstName: first, lastName: last });
                                  }}
                                  onBlur={saveCurrent}
                                />
                              </div>
                              <div style={{ flex: 1, minWidth: 140 }}>
                                <TextField
                                  label="Role"
                                  value={m.role ?? ''}
                                  placeholder="e.g. trustee"
                                  onChange={(v) => setEntityMember(po.ownerIndex, mi, { role: v })}
                                  onBlur={saveCurrent}
                                />
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                              <div style={{ flex: 1, minWidth: 140 }}>
                                <TextField
                                  label="Phone (optional)"
                                  type="tel"
                                  value={m.phone ?? ''}
                                  onChange={(v) => setEntityMember(po.ownerIndex, mi, { phone: v })}
                                  onBlur={saveCurrent}
                                />
                              </div>
                              <div style={{ flex: 1, minWidth: 140 }}>
                                <TextField
                                  label="Email (optional)"
                                  type="email"
                                  value={m.email ?? ''}
                                  onChange={(v) => setEntityMember(po.ownerIndex, mi, { email: v })}
                                  onBlur={saveCurrent}
                                />
                              </div>
                            </div>
                            {/* §14 — should they see statements + financials in the owner portal?
                                Only when the API asks (an API without §14 never sees the field). */}
                            {po.askMemberPortalAccess === true ? (
                              <MemberPortalAccess
                                member={m}
                                onChange={(v) => setMemberPortalAccess(po.ownerIndex, mi, v)}
                              />
                            ) : null}
                            <div>
                              <button
                                type="button"
                                className="btn"
                                data-variant="link"
                                data-size="sm"
                                onClick={() => removeEntityMember(po.ownerIndex, mi)}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                    <div style={{ marginTop: members.length > 0 ? 'var(--space-2)' : 0 }}>
                      <button
                        type="button"
                        className="btn"
                        data-variant="secondary"
                        data-size="sm"
                        onClick={() => addEntityMember(po.ownerIndex)}
                      >
                        + Add a member or trustee
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        {/* City utilities form (docs/city-utility-agreement-plan.md §4): asked only for a
            property inside the City of Walla Walla limits, and never required. */}
        {view.showCityUtilityAccountNo ? (
          <div style={{ marginTop: 'var(--space-5)' }} data-testid="city-utility-account-no">
            <TextField
              label={CITY_UTILITY_ACCOUNT_NO_LABEL}
              value={answers.cityUtilityAccountNo ?? ''}
              onChange={(v) => setAnswers(withCityUtilityAccountNo(answers, v))}
              onBlur={saveCurrent}
            />
            <p className="helper" style={{ marginTop: 'var(--space-2)', color: 'var(--fg-muted)' }}>
              {CITY_UTILITY_ACCOUNT_NO_HELP}
            </p>
          </div>
        ) : null}

        <div style={{ marginTop: 'var(--space-4)' }}>
          <button
            type="button"
            className="btn"
            data-variant="link"
            data-size="sm"
            onClick={() => setShowAddOwnerNote((s) => !s)}
          >
            + Add another owner
          </button>
          {showAddOwnerNote ? (
            // The owner roster is FIXED by the signed management agreement — the intake
            // confirms it, it can't mint a new legal owner (no signature/roster path).
            <p className="helper" style={{ marginTop: 'var(--space-2)', color: 'var(--fg-muted)' }}>
              The owners here come from your signed management agreement. To add another owner, just
              reply to the email we sent you and we&apos;ll take care of it together.
            </p>
          ) : null}
        </div>
      </SectionCard>

      {/* 2 (§13) — contact-only: no banking questions, one line saying so. */}
      {flow === 'contact_only' ? (
        <p style={{ color: 'var(--fg-muted)', margin: '0 0 var(--space-5)' }} data-testid="contact-only-funds">
          {CONTACT_ONLY_FUNDS_LINE}
        </p>
      ) : null}

      {/* 2 (§13) — married filing jointly + funds that follow ownership. */}
      {flow === 'units' ? (
        <SectionCard
          title="Funds & year-end taxes"
          description="These choices are only about banking and your year-end 1099 — every owner listed stays on the agreement and can act on the account."
        >
          <FundsUnitsSection
            key={`${rosterVersion}#${rosterShapeKey(applicable.perOwner)}`}
            applicable={applicable}
            answers={answers}
            onSpousesChange={setSpouses}
            onOwnershipChange={(i, v) =>
              setAnswers(setOwnerPercent(answers, count, i, 'ownershipPercent', v))
            }
            onOwnershipBlur={saveCurrent}
            onToggle={(i, key, v) => commit(setOwnerBool(answers, count, i, key, v), false)}
            onAccountLabelChange={setAccountLabel}
            onAccountLabelBlur={saveCurrent}
          />
        </SectionCard>
      ) : null}

      {/* 2 — Funds + year-end 1099. Single owner: declarative line. Multi: the fork.
          (The legacy section — only when fundsFlow is 'legacy'.) */}
      {flow !== 'legacy' ? null : applicable.showHandlingFork ? (
        <SectionCard
          title="Funds & year-end taxes"
          description="These choices are only about banking and your year-end 1099 — every owner listed stays on the agreement and can act on the account."
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            <label
              style={{ ...radioTileStyle(rawMode === 'one_handles'), alignItems: 'flex-start' }}
            >
              <input
                type="radio"
                name="oc-mode"
                checked={rawMode === 'one_handles'}
                onChange={() => setMode('one_handles')}
                style={{ marginTop: 4 }}
              />
              <span>
                <span style={{ display: 'block', fontWeight: 600, color: 'var(--fg)' }}>
                  One of us handles it
                </span>
                <span className="helper" style={{ color: 'var(--fg-muted)' }}>
                  One deposit, one 1099 — most couples choose this.
                </span>
              </span>
            </label>
            <label style={{ ...radioTileStyle(rawMode === 'split'), alignItems: 'flex-start' }}>
              <input
                type="radio"
                name="oc-mode"
                checked={rawMode === 'split'}
                onChange={() => setMode('split')}
                style={{ marginTop: 4 }}
              />
              <span>
                <span style={{ display: 'block', fontWeight: 600, color: 'var(--fg)' }}>
                  Split between owners
                </span>
                <span className="helper" style={{ color: 'var(--fg-muted)' }}>
                  Funds and the 1099 are divided by percentage.
                </span>
              </span>
            </label>
          </div>

          {/* one_handles → a single funds + 1099 designee + editable name-on-check. */}
          {applicable.showDesignee && rawMode === 'one_handles' ? (
            <div style={{ marginTop: 'var(--space-5)' }}>
              <div className="field" style={{ margin: 0 }}>
                <label>Who should receive the funds and the year-end 1099?</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                  {applicable.perOwner.map((po) => {
                    const selected = answers.disbursement?.fundsOwnerIndex === po.ownerIndex;
                    return (
                      <label key={po.role} style={radioTileStyle(selected)}>
                        <input
                          type="radio"
                          name="oc-designee"
                          checked={selected}
                          onChange={() => setDesignee(po.ownerIndex)}
                        />
                        <span style={{ color: 'var(--fg)' }}>{po.name || `Owner ${po.ownerIndex + 1}`}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <div style={{ marginTop: 'var(--space-4)' }}>
                <TextField
                  label="What should the check or deposit reference? (optional)"
                  value={answers.disbursement?.fundsAccountLabel ?? ''}
                  placeholder="e.g. joint checking"
                  onChange={setAccountLabel}
                  onBlur={saveCurrent}
                />
              </div>
            </div>
          ) : null}

          {/* split → per-owner ownership% / funds% grids + per-owner comms toggles. */}
          {applicable.showPercentages ? (
            <div style={{ marginTop: 'var(--space-5)', display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
              {applicable.perOwner.map((po) => {
                const oa = answers.owners?.[po.ownerIndex];
                return (
                  <div
                    key={po.role}
                    style={{
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-md)',
                      padding: 'var(--space-4)',
                    }}
                  >
                    <div style={{ fontWeight: 600, color: 'var(--fg)', marginBottom: 'var(--space-3)' }}>
                      {po.name || `Owner ${po.ownerIndex + 1}`}
                    </div>
                    <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
                      <div className="field" style={{ margin: 0, flex: 1, minWidth: 120 }}>
                        <label>Ownership %</label>
                        <input
                          className="input"
                          type="number"
                          inputMode="decimal"
                          min={0}
                          max={100}
                          value={oa?.ownershipPercent ?? ''}
                          onChange={(e) =>
                            setAnswers(
                              setOwnerPercent(
                                answers,
                                count,
                                po.ownerIndex,
                                'ownershipPercent',
                                parsePercent(e.target.value),
                              ),
                            )
                          }
                          onBlur={saveCurrent}
                        />
                      </div>
                      <div className="field" style={{ margin: 0, flex: 1, minWidth: 120 }}>
                        <label>Funds %</label>
                        <input
                          className="input"
                          type="number"
                          inputMode="decimal"
                          min={0}
                          max={100}
                          value={oa?.fundsPercent ?? ''}
                          onChange={(e) =>
                            setAnswers(
                              setOwnerPercent(
                                answers,
                                count,
                                po.ownerIndex,
                                'fundsPercent',
                                parsePercent(e.target.value),
                              ),
                            )
                          }
                          onBlur={saveCurrent}
                        />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 'var(--space-4)', marginTop: 'var(--space-3)', flexWrap: 'wrap' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                        <input
                          type="checkbox"
                          checked={oa?.receivesStatements !== false}
                          onChange={(e) =>
                            commit(
                              setOwnerBool(answers, count, po.ownerIndex, 'receivesStatements', e.target.checked),
                              false,
                            )
                          }
                        />
                        <span className="helper" style={{ color: 'var(--fg-muted)' }}>
                          Receives owner statements
                        </span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                        <input
                          type="checkbox"
                          checked={oa?.receivesEmail !== false}
                          onChange={(e) =>
                            commit(
                              setOwnerBool(answers, count, po.ownerIndex, 'receivesEmail', e.target.checked),
                              false,
                            )
                          }
                        />
                        <span className="helper" style={{ color: 'var(--fg-muted)' }}>
                          Receives email
                        </span>
                      </label>
                    </div>
                  </div>
                );
              })}
              <div className="helper" style={{ color: ownershipOk && fundsOk ? 'var(--fg-muted)' : 'var(--danger)' }}>
                Ownership adds to {ownershipSum || 0}% · Funds add to {fundsSum || 0}% — each needs to
                total 100%.
              </div>
            </div>
          ) : null}
        </SectionCard>
      ) : (
        // Single owner — no percentages. One declarative, editable line (D9: statements
        // + email default to yes, not asked).
        <SectionCard title="Funds & year-end taxes">
          <p style={{ color: 'var(--fg)', margin: 0 }}>
            Your monthly funds and your year-end 1099 go to <strong>{soleName}</strong>
            {answers.disbursement?.fundsAccountLabel
              ? <>; the deposit references <strong>{answers.disbursement.fundsAccountLabel}</strong></>
              : null}
            .
          </p>
          <div style={{ marginTop: 'var(--space-3)' }}>
            <button
              type="button"
              className="btn"
              data-variant="link"
              data-size="sm"
              onClick={() => setShowAccountLabel((s) => !s)}
            >
              {showAccountLabel ? 'Done' : 'Change'}
            </button>
          </div>
          {showAccountLabel ? (
            <div style={{ marginTop: 'var(--space-3)' }}>
              <TextField
                label="What should the check or deposit reference? (optional)"
                value={answers.disbursement?.fundsAccountLabel ?? ''}
                placeholder="e.g. personal checking"
                onChange={setAccountLabel}
                onBlur={saveCurrent}
              />
            </div>
          ) : null}
        </SectionCard>
      )}

      {/* 3 — Backup contacts (optional, non-owner). */}
      <SectionCard
        title="Backup contacts (optional)"
        description="Someone we can reach for urgent questions if we can't reach the owner(s). They're not owners — they don't sign anything or receive funds."
      >
        {backups.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {backups.map((b, i) => (
              <div
                key={i}
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-4)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--space-3)',
                }}
              >
                <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <TextField
                      label="Name"
                      value={b.name}
                      onChange={(v) => setBackup(i, { name: v })}
                      onBlur={saveCurrent}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <TextField
                      label="Relationship to the owner(s)"
                      value={b.relationship}
                      placeholder="e.g. son, property manager"
                      onChange={(v) => setBackup(i, { relationship: v })}
                      onBlur={saveCurrent}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <TextField
                      label="Phone (optional)"
                      type="tel"
                      value={b.phone ?? ''}
                      onChange={(v) => setBackup(i, { phone: v })}
                      onBlur={saveCurrent}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <TextField
                      label="Email (optional)"
                      type="email"
                      value={b.email ?? ''}
                      onChange={(v) => setBackup(i, { email: v })}
                      onBlur={saveCurrent}
                    />
                  </div>
                </div>
                <div>
                  <button
                    type="button"
                    className="btn"
                    data-variant="link"
                    data-size="sm"
                    onClick={() => removeBackup(i)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : null}
        <div style={{ marginTop: backups.length > 0 ? 'var(--space-4)' : 0 }}>
          <button type="button" className="btn" data-variant="secondary" data-size="sm" onClick={addBackup}>
            + Add a backup contact
          </button>
        </div>
      </SectionCard>

      {/* 4 — How you found us (referral). Source required; referrer detail optional. */}
      <SectionCard title="How did you find us?">
        <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          {REFERRAL_SOURCES.map((s) => {
            const selected = source === s.value;
            return (
              <label key={s.value} style={radioTileStyle(selected)}>
                <input
                  type="radio"
                  name="oc-source"
                  checked={selected}
                  onChange={() => setSource(s.value)}
                />
                <span style={{ color: 'var(--fg)' }}>{s.label}</span>
              </label>
            );
          })}
        </div>
        {source === REFERRAL_REFERRAL ? (
          <div style={{ marginTop: 'var(--space-4)' }}>
            {/* Copy approved by Miles 2026-09-25 — the contract stores ONE free-text referrer
                detail (referral.referrerDetail); name/phone/email fold into it here. */}
            <TextField
              label="Who can we thank? (optional — a name, and a phone or email if you have it)"
              value={answers.referral?.referrerDetail ?? ''}
              onChange={setReferrerDetail}
              onBlur={saveCurrent}
            />
          </div>
        ) : null}
      </SectionCard>

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 'var(--space-3)',
          marginTop: 'var(--space-6)',
        }}
      >
        <span className="helper" style={{ color: 'var(--fg-muted)' }}>
          {saving ? 'Saving…' : 'Your answers save automatically.'}
        </span>
        <button
          className="btn"
          data-size="lg"
          disabled={submitting}
          onClick={() => void handleComplete()}
        >
          {submitting ? 'Preparing…' : 'Complete & sign'}
        </button>
      </div>
    </>,
  );
}
